<template>
  <div v-if="visit && visit.id" data-component="Lot/NewVisit" class="container is-fluid is-fiche-visite">
    <header class="is-flex">
      <h2>Visite du {{ formatDate(visit.createdAt) }}</h2>
      <div class="header-actions">
        <button
          v-if="isAllowedToDelete"
          class="delete-visit-btn"
          :class="{ 'is-loading': deletingVisit }"
          :disabled="deletingVisit"
          @click="showDeleteConfirm = true"
        >
          <span v-if="!deletingVisit" class="delete-visit-btn__inner">
            <svg class="delete-visit-btn__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M9 3h6M3 6h18M19 6l-1 14H6L5 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
            Supprimer
          </span>
          <span v-else class="delete-visit-btn__inner">
            <span class="delete-visit-btn__spinner" aria-hidden="true"></span>
            Suppression…
          </span>
        </button>
        <CloseButton :to="`/dossier/${propertyId}/timeline`" :doBack="false" />
      </div>
    </header>

    <!-- Modale de confirmation de suppression -->
    <Transition name="delete-modal">
      <div
        v-if="showDeleteConfirm"
        class="delete-modal-overlay"
        @click.self="showDeleteConfirm = false"
      >
        <div class="delete-modal-card" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
          <div class="delete-modal-card__icon" aria-hidden="true">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="23" stroke="currentColor" stroke-width="2"/>
              <path d="M17 19h14M21 14h6M22 24v7M26 24v7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M18 19l1 16h10l1-16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 id="delete-modal-title" class="delete-modal-card__title">Supprimer la visite ?</h3>
          <p class="delete-modal-card__desc">
            La visite du <strong>{{ formatDate(visit.createdAt) }}</strong> sera définitivement supprimée.
            Cette action est irréversible.
          </p>
          <div class="delete-modal-card__actions">
            <button
              class="delete-modal-card__btn delete-modal-card__btn--cancel"
              @click="showDeleteConfirm = false"
            >
              Annuler
            </button>
            <button
              class="delete-modal-card__btn delete-modal-card__btn--confirm"
              :disabled="deletingVisit"
              @click="doDeleteVisit"
            >
              <span v-if="!deletingVisit">Supprimer</span>
              <span v-else class="delete-modal-card__btn-spinner" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <hr class="is-short-line" />

    <!-- Listing -->
    <div class="listing">
      <!-- Summary section -->
      <div class="summary">
        <div class="line">
          <p class="sub-title">Le logement est relouable ... <strong>{{ visit.rentable }}</strong></p>
          <hr />
          <div class="is-flex has-item-started">
          </div>
          <div class="is-flex">
            <p class="sub-title">Y a-t-il du désordre / des travaux à prévoir ? <strong>{{ visit.workToBePlanned }}</strong></p>
          </div>
          <template v-if="visit.comment || visit.workToBePlannedPictures.length > 0">
            <hr />
            <div class="comment-wrapper">
              <p v-if="visit.comment" class="content">
                {{ visit.comment }}
              </p>
              <div v-if="visit.workToBePlannedPictures.length" class="picture-area">
                <template v-for="(picture, i) in visit.workToBePlannedPictures" :key="`picture-${i}-${visit.id}`">
                  <div v-if="picture.loading" id="image-loader">
                    <img :src="loadingGif" class="wrappedImage" alt="chargement" />
                  </div>
                  <div v-else-if="picture.offline" class="offline-image-placeholder">
                    <p>📵</p>
                    <p class="offline-text">{{ picture.error || 'Image non disponible hors ligne' }}</p>
                  </div>
                  <img
                    v-else-if="picture.thumbnail"
                    :src="picture.thumbnail"
                    :alt="`n°${i}`"
                    @click="displayImages(i, getDisplayImages(visit.workToBePlannedPictures))"
                  />
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Listing table header -->
      <div class="header">
        <div class="flex">
          <div class="flex-1">
            <p>Points de contrôle</p>
          </div>
          <div class="flex-1 text-right">
            <p>ACTIONS</p>
          </div>
        </div>
      </div>

      <!-- Listing table body -->
      <div class="body">
        <div 
          v-for="(control, i) in visit.controls" 
          :key="`line-${control.id}`" 
          class="line"
        >
          <div class="columns">
            <!-- First column contains title / category and points -->
            <div class="column is-flex">
              <div class="contains-title-and-category">
                <p class="is-category">{{ i + 1 }} - {{ control.category }}</p>
                <p class="is-title">{{ control.label }}</p>
              </div>

              <div class="contains-points is-flex">
                <ControlPoint 
                  v-for="(point, i2) in control.points" 
                  :key="`point-${control.id}-${point.value || i2}`"
                  :name="`point-${control.id}-${i2}`"
                  :id="`point-${control.id}-${point.value || i2}`"
                  :label="point.label"
                  :value="point.value || i2"
                  :checked="point.selected"
                  :readOnly="true"
                />
              </div>
            </div>

            <!-- Second column contains action checkbox -->
            <div class="column is-checkbox is-flex has-item-centered">
              <Checkbox
                :id="`check-line-${control.id}-${i}`"
                :modelValue="control.checked"
                :readOnly="true"
              />
            </div>

            <!-- Third column contains comment and pictures buttons -->
            <div class="column contains-comments-and-pictures is-flex" style="width: 94px;">
              <p 
                v-if="control.hasCommentContent"
                :class="['comment', 'active']"
                @click="toggleCommentsPane(i)"
              >
                <i class="ico comment"></i>
                <i class="has-comment"></i>
              </p>
              <p 
                v-if="control.hasPictureContent"
                :class="['pictures', 'active']"
                @click="toggleCommentsPane(i)"
              >
                <i class="ico photo"></i>
                <i class="has-comment"></i>
              </p>
            </div>
          </div>

          <!-- Comment pane -->
          <div :class="['is-comment-pane', { open: control.commentsPaneOpen, close: !control.commentsPaneOpen }]">
            <template v-if="control.comments && control.comments.length > 0">
              <div 
                v-for="comment in control.comments" 
                :key="`comment-${comment.id}`"
                class="comment-wrapper"
              >
                <p class="author">
                  {{ comment.author }},&nbsp;
                  <span class="at">{{ formatDate(comment.createdAt) }}</span>
                </p>
                <p v-if="comment.text" class="content">
                  {{ comment.text }}
                </p>
                <div v-if="comment.pictures.length" class="picture-area">
                  <template v-for="(picture, i) in comment.pictures" :key="`picture-${i}-${comment.id}`">
                    <div v-if="picture.loading" id="image-loader">
                      <img :src="loadingGif" class="wrappedImage" alt="chargement" />
                    </div>
                    <div v-else-if="picture.offline" class="offline-image-placeholder">
                      <p>📵</p>
                      <p class="offline-text">{{ picture.error || 'Image non disponible hors ligne' }}</p>
                    </div>
                    <img
                      v-else-if="picture.thumbnail"
                      :src="picture.thumbnail"
                      :alt="`n°${i}`"
                      @click="displayImages(i, getDisplayImages(comment.pictures))"
                    />
                  </template>
                </div>
              </div>
            </template>
            <div v-else class="comment-wrapper">
              <p class="no-comment">Aucun commentaire</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import Checkbox from '@/components/Widget/Checkbox.vue'
import CloseButton from '@/components/Widget/CloseButton.vue'
import ControlPoint from '@/components/Widget/ControlPoint.vue'
import { QUALITY_POINTS, YESNO_POINTS, RENTABLE_POINTS, RENTABLE_FORM_POINTS, YESNO_FORM_POINTS } from '@/utils/constants'
import PROGRESS_STATUS from '@/utils/progressStatus'
import '@/assets/styles/offline-images.css'

export default {
  name: 'LotVisit',
  components: {
    Checkbox,
    CloseButton,
    ControlPoint
  },
  data() {
    return {
      loadingGif: '/images/loading.gif',
      visit: null,
      loading: false,
      isOnline: navigator.onLine,
      deletingVisit: false,
      showDeleteConfirm: false,
    }
  },
  computed: {
    ...mapState('lot', ['currentLot']),
    
    propertyId() {
      return this.$route.params.id
    },
    
    visitId() {
      return this.$route.params.visitId
    },

    isAllowedToDelete() {
      const user = this.$store?.state?.login?.user || this.$store?.getters?.['user/currentUser']
      if (!user || !user.username) return false
      return ['boris.parejo', 'igor.davidovitch'].includes(user.username)
    },
  },
  methods: {
    ...mapActions('visit', [
      'fetchVisit',
      'toggleCommentsPane'
    ]),
    
    toggleCommentsPane(index) {
      if (this.visit && this.visit.controls[index]) {
        this.visit.controls[index].commentsPaneOpen = !this.visit.controls[index].commentsPaneOpen
      }
    },
    
    hasComments(comments) {
      return comments && comments.length > 0 && comments.some(comment => comment.text && comment.text.trim())
    },
    
    hasPictures(comments) {
      return comments && comments.length > 0 && comments.some(comment => comment.pictures && comment.pictures.length > 0)
    },
    
    hasAnyContent(comments) {
      return this.hasComments(comments) || this.hasPictures(comments)
    },
    
    getDisplayImages(pictures) {
      return pictures.map(picture => ({
        src: picture.raw || picture.thumbnail,
        alt: ''
      }))
    },
    
    displayImages(/* index, images */) {
      // Ouvrir la visionneuse d'images
      // Ici on devrait dispatcher l'action pour ouvrir la visionneuse
    },
    
    async doDeleteVisit() {
      this.deletingVisit = true
      try {
        // 1. Suppression côté API
        const api = (await import('../../services/api')).default
        const result = await api.deleteVisit(this.visit.id)

        if (!result.success) {
          this.showDeleteConfirm = false
          alert(`Erreur lors de la suppression : ${result.error || result.message}`)
          return
        }

        // 2. Retirer la visite du cache IndexedDB local
        try {
          const dexieService = (await import('../../services/dexieService')).default
          const property = await dexieService.getProperty(this.propertyId)
          if (property && Array.isArray(property.visits)) {
            property.visits = property.visits.filter(v => v.id !== this.visit.id)
            await dexieService.addProperty(property) // put + invalidation du cache
          }
        } catch (cacheError) {
          // Non bloquant : la suppression API a réussi, la synchro corrigera le cache
          console.warn('Impossible de mettre à jour le cache local:', cacheError)
        }

        // 3. Rafraîchir la timeline dans le store Vuex avant navigation
        await this.$store.dispatch('timeline/fetchTimelineEvents', this.propertyId)

        this.showDeleteConfirm = false
        this.$router.push(`/dossier/${this.propertyId}/timeline`)

      } catch (error) {
        console.error('Erreur suppression visite:', error)
        this.showDeleteConfirm = false
        alert(`Erreur lors de la suppression : ${error.message}`)
      } finally {
        this.deletingVisit = false
      }
    },

    formatDate(dateString) {
      return moment(dateString).format('DD/MM/YYYY')
    },
    
    updateOnlineStatus() {
      this.isOnline = navigator.onLine
    },
    
    async loadVisit() {
      this.loading = true
      
      try {
        // Charger la visite depuis IndexedDB
        const dexieService = (await import('../../services/dexieService')).default
        
        // Récupérer la propriété depuis IndexedDB
        const property = await dexieService.getProperty(this.propertyId)
        
        let visitData = null
        
        // 1. D'abord chercher dans property.visits (visites synchronisées)
        if (property && property.visits && property.visits.length > 0) {
          visitData = property.visits.find(visit => visit.id === this.visitId)
        }
        
        // 2. Si non trouvée, chercher dans les mutations (visites non synchronisées)
        if (!visitData) {
          try {
            const { mutations } = await import('@/services/dexieService')
            const pendingVisitMutations = await mutations()
              .filter(mutation => {
                return mutation.entity === 'visit' && 
                       mutation.data && 
                       (mutation.data.propertyId === this.propertyId || 
                        mutation.data.property?.id === this.propertyId)
              })
              .toArray()
            
            // Chercher la visite par ID dans les mutations
            const visitMutation = pendingVisitMutations.find(mutation => {
              return mutation.data.id === this.visitId
            })
            
            if (visitMutation) {
              visitData = visitMutation.data
            }
          } catch (mutationError) {
            console.error('❌ Erreur lors de la récupération depuis les mutations:', mutationError)
          }
        }
        
        if (!visitData) {
          return
        }

        // ── Chargement des photos à la demande (online only) ──────────────────
        // Les photos (workToBePlannedPicturesUrl + comment.picturesUrl) ne sont plus
        // embarquées dans l'export ni la sync incrémentale pour réduire la taille du
        // fichier gzip. On les récupère depuis l'API au moment où l'utilisateur ouvre
        // la visite, uniquement si la connexion est disponible et si ce n'est pas une
        // mutation locale (qui porte déjà ses photos en base64 dans picturesPane).
        if (navigator.onLine && visitData.id && !visitData.summary?.restoration?.picturesPane) {
          try {
            const apiService = (await import('../../services/api')).default
            const resp = await apiService.request(`/visits/${visitData.id}/photos`)
            if (resp && (resp.workToBePlannedPicturesUrl !== undefined || resp.comments !== undefined)) {
              const { workToBePlannedPicturesUrl: wbppUrl, comments: commentPhotos } = resp
              // Injecter les filenames de photos de restauration
              if (Array.isArray(wbppUrl) && wbppUrl.length > 0) {
                visitData.workToBePlannedPicturesUrl = wbppUrl
              }
              // Injecter les filenames de photos de commentaires (keyed by commentId)
              if (commentPhotos && typeof commentPhotos === 'object' && Array.isArray(visitData.controls)) {
                visitData.controls.forEach(control => {
                  (control.comments || []).forEach(comment => {
                    if (commentPhotos[comment.id] && commentPhotos[comment.id].length > 0) {
                      comment.picturesUrl = commentPhotos[comment.id]
                    }
                  })
                })
              }
            }
          } catch (err) {
            console.warn('[Visit] Photos non disponibles (mode dégradé) :', err.message)
          }
        }

        // Transformer les données de visite pour correspondre au format attendu
        // Gérer différents formats de données (visites synchronisées vs mutations)
        const rentable = await this.getRentableLabel(visitData)
        const workToBePlanned = await this.getWorkToBePlannedLabel(visitData)
        
        // Gérer les deux formats : controls (synchronisé) et controlPoints (mutations)
        const controlsData = visitData.controls || visitData.controlPoints || []
        
        // Récupérer le commentaire (format mutation vs format synchronisé)
        const comment = visitData.summary?.restoration?.comment || 
                       visitData.comment || 
                       ''
        
        // Récupérer les images (format mutation vs format synchronisé)
        let workToBePlannedPictures = []
        if (visitData.summary?.restoration?.picturesPane?.pictures) {
          // Format mutation
          workToBePlannedPictures = await this.transformPicturesFromMutation(visitData.summary.restoration.picturesPane.pictures)
        } else if (visitData.workToBePlannedPicturesUrl) {
          // Format synchronisé — les photos de restauration sont dans 'visit/work-to-be-planned'
          workToBePlannedPictures = await this.transformPictures(visitData.workToBePlannedPicturesUrl, 'visit/work-to-be-planned')
        }
        
        this.visit = {
          id: visitData.id,
          propertyId: this.propertyId,
          createdAt: visitData.createdAt || visitData.timestamp,
          rentable,
          workToBePlanned,
          comment,
          workToBePlannedPictures,
          controls: await this.transformControls(controlsData)
        }
        
      } catch (error) {
        console.error('❌ Erreur lors du chargement de la visite:', error)
        console.error('Stack trace:', error.stack)
      } finally {
        this.loading = false
      }
    },
    
    transformPictures(picturesUrl, uploadDir = 'comment') {
      if (!Array.isArray(picturesUrl)) return []

      const getBaseUrl = () => {
        const apiUrl = (window.env && window.env.VUE_APP_API_URL && window.env.VUE_APP_API_URL !== '__VUE_APP_API_URL__')
          ? window.env.VUE_APP_API_URL
          : (process.env.VUE_APP_API_URL || '/api')
        // Retirer le suffixe /api pour obtenir l'URL racine publique
        return apiUrl.replace(/\/api$/, '')
      }

      return picturesUrl.map((picture, index) => {
        let filename = null

        // Si picture est une simple string (nom de fichier ou URL directe)
        if (typeof picture === 'string') {
          filename = picture
        } else {
          // Si picture est un objet avec thumbnail/raw
          filename = picture.thumbnail || picture.raw
        }

        if (!filename) {
          return { id: `${index + 1}`, thumbnail: null, raw: null, loading: false }
        }

        // Si c'est deja une data URL (base64), pas de transformation
        if (filename.startsWith('data:')) {
          return { id: `${index + 1}`, thumbnail: filename, raw: filename, loading: false }
        }

        // Si c'est deja une URL complete, la garder telle quelle
        if (filename.startsWith('http://') || filename.startsWith('https://')) {
          return { id: `${index + 1}`, thumbnail: filename, raw: filename, loading: false }
        }

        // Sinon c'est un nom de fichier : construire l'URL publique directe
        // Correspond a UrlResolver::getUploadUri() cote API
        const url = `${getBaseUrl()}/uploads/${uploadDir}/${filename}`
        return { id: `${index + 1}`, thumbnail: url, raw: url, loading: false }
      })
    },
    
    async transformControls(controls) {
      if (!Array.isArray(controls)) {
        return []
      }
      
      // Récupérer les interventionPoints depuis IndexedDB pour obtenir les vrais libellés
      const dexieService = (await import('../../services/dexieService')).default
      const interventionPoints = await dexieService.db.table('interventionPoints').toArray()
      
      // Créer un map pour accès rapide par ID et position
      const interventionPointsMap = new Map(
        interventionPoints.map(ip => [ip.id, ip])
      )
      
      // Trier les contrôles selon la même logique que dans la création
      // 1er critère : position (ordre ascendant)
      // 2nd critère : id (ordre ascendant, en cas d'égalité de position)
      const sortedControls = [...controls].sort((a, b) => {
        // Récupérer les interventionPoints correspondants pour accéder à leur position
        const interventionPointIdA = a.interventionPoint?.id || a.id
        const interventionPointIdB = b.interventionPoint?.id || b.id
        
        const ipA = interventionPointsMap.get(interventionPointIdA)
        const ipB = interventionPointsMap.get(interventionPointIdB)
        
        // Comparer d'abord par position
        const positionA = ipA?.position !== undefined ? ipA.position : Infinity
        const positionB = ipB?.position !== undefined ? ipB.position : Infinity
        
        if (positionA !== positionB) {
          return positionA - positionB
        }
        
        // Si les positions sont égales, comparer par id
        const idA = interventionPointIdA
        const idB = interventionPointIdB
        
        // Comparaison numérique si les ids sont des nombres
        if (typeof idA === 'number' && typeof idB === 'number') {
          return idA - idB
        }
        
        // Sinon comparaison lexicographique
        return String(idA).localeCompare(String(idB))
      })
      
      // Utiliser Promise.all pour gérer les transformations asynchrones
      return Promise.all(sortedControls.map(async (control, index) => {
        // Pour les mutations, essayer de récupérer l'interventionPoint par ID
        let interventionPoint = null
        
        // L'ID de l'interventionPoint peut être dans différents endroits
        const interventionPointId = control.interventionPoint?.id || control.id
        
        if (interventionPointId && interventionPointsMap.has(interventionPointId)) {
          interventionPoint = interventionPointsMap.get(interventionPointId)
        }
        
        // Gérer différentes structures de données
        // Prioriser les données de l'interventionPoint si disponible, sinon utiliser les données du control
        let category = interventionPoint?.section?.name ||
                      interventionPoint?.category ||
                      control.category ||
                      control.section?.name || 
                      control.interventionPoint?.section?.name || 
                      control.interventionPoint?.category ||
                      `Contrôle ${index + 1}`
        
        let label = interventionPoint?.label || 
                    interventionPoint?.title ||
                    interventionPoint?.name ||
                    control.title ||
                    control.label || 
                    control.name || 
                    control.interventionPoint?.label ||
                    control.interventionPoint?.title ||
                    `Point de contrôle ${index + 1}`
        
        // Récupérer le answerType depuis l'interventionPoint
        const answerType = control.interventionPoint?.answerType || 
                          control.answerType || 
                          'answer_state'
        
        // Gérer les commentaires des deux formats
        let comments = []
        let hasCommentContent = false
        let hasPictureContent = false
        
        if (control.comments && Array.isArray(control.comments)) {
          // Format synchronisé : tableau de commentaires
          comments = await this.transformComments(control.comments)
          hasCommentContent = comments.some(c => c.text && c.text.trim())
          hasPictureContent = comments.some(c => c.pictures && c.pictures.length > 0)
        } else if (control.commentPane || control.picturesPane) {
          // Format mutation : commentPane avec value et pictures
          // Vérifier s'il y a du contenu
          hasCommentContent = !!(control.commentPane && control.commentPane.value && control.commentPane.value.trim())
          hasPictureContent = !!(control.picturesPane && control.picturesPane.pictures && control.picturesPane.pictures.length > 0)
          
          // Créer un commentaire unique depuis le commentPane si il y a du contenu
          if (hasCommentContent || hasPictureContent) {
            comments = [{
              id: `comment-${control.id}`,
              author: 'Vous',
              createdAt: new Date().toISOString(),
              text: control.commentPane?.value || '',
              pictures: control.picturesPane?.pictures ? await this.transformPicturesFromMutation(control.picturesPane.pictures) : []
            }]
          }
        }
        
        const transformed = {
          id: control.id || `control-${index}`,
          category,
          label,
          checked: control.checked || 
                  control.actionStatus === PROGRESS_STATUS.TODO || 
                  control.actionStatus === PROGRESS_STATUS.DOING || 
                  control.action === true ||
                  false,
          commentsPaneOpen: false,
          points: control.points || this.generatePointsFromAnswer(control.answer || control.response || control.value, answerType),
          comments,
          hasCommentContent,
          hasPictureContent
        }
        
        return transformed
      }))
    },
    
    generatePointsFromAnswer(answer, answerType = 'answer_state') {
      // Déterminer les points disponibles en fonction du answerType
      let availablePoints
      if (answerType === 'answer_yesno') {
        availablePoints = YESNO_POINTS.map(p => ({ ...p, selected: false }))
      } else {
        // Par défaut, answer_state
        availablePoints = QUALITY_POINTS.map(p => ({ ...p, selected: false }))
      }
      
      // Marquer le point correspondant à la réponse (valeur ou label)
      if (answer) {
        // Chercher d'abord par valeur exacte
        const pointByValue = availablePoints.find(p => p.value === answer)
        if (pointByValue) {
          pointByValue.selected = true
          return availablePoints
        }
        
        // Sinon, chercher par label (conversion en minuscules pour comparaison)
        const answerLower = String(answer).toLowerCase()
        const pointByLabel = availablePoints.find(p => 
          p.label.toLowerCase() === answerLower ||
          p.label.toLowerCase().includes(answerLower) ||
          answerLower.includes(p.label.toLowerCase())
        )
        
        if (pointByLabel) {
          pointByLabel.selected = true
        } else {
          // Par défaut, marquer le premier point si pas de correspondance
          availablePoints[0].selected = true
        }
      } else {
        // Par défaut, marquer le premier point si pas de réponse
        availablePoints[0].selected = true
      }
      
      return availablePoints
    },
    
    async transformComments(comments) {
      if (!Array.isArray(comments)) return []
      
      // Récupérer tous les utilisateurs depuis IndexedDB
      const dexieService = (await import('../../services/dexieService')).default
      const users = await dexieService.db.table('users').toArray()
      
      // Créer un map pour accès rapide par ID
      const usersMap = new Map(users.map(user => [user.id, user]))
      
      return Promise.all(comments.map(async comment => {
        let authorName = 'Utilisateur'
        
        // Récupérer l'utilisateur par authorId
        if (comment.authorId && usersMap.has(comment.authorId)) {
          const user = usersMap.get(comment.authorId)
          authorName = user.fullName || 
                      `${user.firstName || ''} ${user.lastName || ''}`.trim() || 
                      'Utilisateur'
        } else if (comment.author) {
          // Fallback si l'objet author est disponible directement
          authorName = comment.author.fullName || 
                      `${comment.author.firstName || ''} ${comment.author.lastName || ''}`.trim() || 
                      'Utilisateur'
        }
        
        return {
          id: comment.id,
          author: authorName,
          createdAt: comment.createdAt,
          text: comment.text || '',
          pictures: await this.transformPictures(comment.picturesUrl || [], 'comment')
        }
      }))
    },
    
    async transformPicturesFromMutation(pictures) {
      if (!Array.isArray(pictures)) return []

      const getBaseUrl = () => {
        const apiUrl = (window.env && window.env.VUE_APP_API_URL && window.env.VUE_APP_API_URL !== '__VUE_APP_API_URL__')
          ? window.env.VUE_APP_API_URL
          : (process.env.VUE_APP_API_URL || '/api')
        return apiUrl.replace(/\/api$/, '')
      }

      return pictures.map((picture, index) => {
        const originalUrl = picture.src || picture.file?.src
        const pictureId = picture.id || `${index + 1}`

        if (!originalUrl) {
          return { id: pictureId, thumbnail: null, raw: null, loading: false }
        }

        // Les photos de mutations sont toujours en base64 (data:)
        if (originalUrl.startsWith('data:')) {
          return { id: pictureId, thumbnail: originalUrl, raw: originalUrl, loading: false }
        }

        // URL complete : garder telle quelle
        if (originalUrl.startsWith('http://') || originalUrl.startsWith('https://')) {
          return { id: pictureId, thumbnail: originalUrl, raw: originalUrl, loading: false }
        }

        // Nom de fichier simple : construire l'URL directe
        const url = `${getBaseUrl()}/uploads/comment/${originalUrl}`
        return { id: pictureId, thumbnail: url, raw: url, loading: false }
      })
    },
    async getRentableLabel(visitData) {
      // Gérer le format avec summary.rentable (visites des mutations)
      if (visitData.summary && visitData.summary.rentable) {
        const selectedRentable = visitData.summary.rentable.find(r => r.selected)
        return selectedRentable ? selectedRentable.label : 'Non défini'
      }
      
      // Format classique (visites synchronisées)
      const rentableValue = visitData.rentable
      
      if (rentableValue === null || rentableValue === undefined) return 'Non défini'
      
      // Chercher d'abord dans RENTABLE_FORM_POINTS (valeurs numériques 1, 2, 3)
      // Utiliser == au lieu de === pour gérer la conversion string/number
      const formPoint = RENTABLE_FORM_POINTS.find(point => 
        point.value == rentableValue || point.label === rentableValue
      )
      if (formPoint) return formPoint.label
      
      // Sinon chercher dans RENTABLE_POINTS (valeurs 'I', 'NW', 'NHW')
      const apiPoint = RENTABLE_POINTS.find(point => 
        point.value == rentableValue || point.label === rentableValue
      )
      if (apiPoint) return apiPoint.label
      
      // Retourner la valeur telle quelle si aucune correspondance
      return String(rentableValue)
    },
    
    async getWorkToBePlannedLabel(visitData) {
      // Gérer le format avec summary.restoration (visites des mutations)
      if (visitData.summary && visitData.summary.restoration) {
        const selectedRestoration = visitData.summary.restoration.inSummary?.find(r => r.selected)
        return selectedRestoration ? selectedRestoration.label : 'Non défini'
      }
      
      // Format classique (visites synchronisées)
      const workValue = visitData.workToBePlanned
      
      if (workValue === null || workValue === undefined) return 'Non défini'
      
      // Chercher d'abord dans YESNO_FORM_POINTS (valeurs numériques 1, 2)
      // Utiliser == au lieu de === pour gérer la conversion string/number
      const formPoint = YESNO_FORM_POINTS.find(point => 
        point.value == workValue || point.label === workValue
      )
      if (formPoint) return formPoint.label
      
      // Sinon chercher dans YESNO_POINTS (valeurs 'Y', 'N', 'U')
      const apiPoint = YESNO_POINTS.find(point => 
        point.value == workValue || point.label === workValue
      )
      if (apiPoint) return apiPoint.label
      
      // Retourner la valeur telle quelle si aucune correspondance
      return String(workValue)
    }
  },
  
  async mounted() {
    await this.loadVisit()
    
    // Écouter les changements de statut en ligne/hors ligne
    window.addEventListener('online', this.updateOnlineStatus)
    window.addEventListener('offline', this.updateOnlineStatus)
  },
  
  beforeUnmount() {
    // Nettoyer les écouteurs d'événements
    window.removeEventListener('online', this.updateOnlineStatus)
    window.removeEventListener('offline', this.updateOnlineStatus)
  }
}
</script>

<style scoped>
/* ── Header ─────────────────────────────────────────────────────────────── */
.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* ── Bouton "Supprimer" dans le header ──────────────────────────────────── */
.delete-visit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 118px;
  height: 32px;
  padding: 0 14px;
  background: transparent;
  border: 1.5px solid #E92662;
  border-radius: 4px;
  color: #E92662;
  font-family: "Arial Rounded MT Bold", Arial, sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.delete-visit-btn:hover:not(:disabled) {
  background: #E92662;
  color: #fff;
}

.delete-visit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.delete-visit-btn__inner {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.delete-visit-btn__icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

/* Spinner dans le bouton header */
.delete-visit-btn__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

/* ── Modale overlay ──────────────────────────────────────────────────────── */
.delete-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 52, 74, 0.45); /* $darkblue à 45% */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* ── Carte modale ────────────────────────────────────────────────────────── */
.delete-modal-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 52, 74, 0.2);
  padding: 36px 32px 28px;
  max-width: 380px;
  width: calc(100% - 48px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.delete-modal-card__icon {
  color: #E92662;
  width: 56px;
  height: 56px;
  margin-bottom: 4px;
}

.delete-modal-card__icon svg {
  width: 100%;
  height: 100%;
}

.delete-modal-card__title {
  font-family: "Arial Rounded MT Bold", Arial, sans-serif;
  font-size: 1.15rem;
  color: #00344A; /* $darkblue */
  margin: 0;
}

.delete-modal-card__desc {
  font-family: "Arial Regular", Arial, sans-serif;
  font-size: 0.875rem;
  color: #9DB3C4; /* $bluegray */
  margin: 0;
  line-height: 1.5;
}

.delete-modal-card__desc strong {
  color: #00344A;
  font-weight: 600;
}

/* ── Boutons de la modale ─────────────────────────────────────────────────── */
.delete-modal-card__actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  width: 100%;
}

.delete-modal-card__btn {
  flex: 1;
  height: 44px;
  border-radius: 4px;
  font-family: "Arial Rounded MT Bold", Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
}

.delete-modal-card__btn--cancel {
  background: transparent;
  border: 1.5px solid #CFDAE2; /* $gray */
  color: #9DB3C4; /* $bluegray */
}

.delete-modal-card__btn--cancel:hover {
  border-color: #9DB3C4;
  color: #6891AC; /* $blue */
}

.delete-modal-card__btn--confirm {
  background: #E92662; /* $pink */
  color: #fff;
  min-width: 48px;
}

.delete-modal-card__btn--confirm:hover:not(:disabled) {
  background: #c41f53;
}

.delete-modal-card__btn--confirm:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Spinner dans le bouton confirm */
.delete-modal-card__btn-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ── Transition modale ───────────────────────────────────────────────────── */
.delete-modal-enter-active,
.delete-modal-leave-active {
  transition: opacity 0.2s ease;
}

.delete-modal-enter-active .delete-modal-card,
.delete-modal-leave-active .delete-modal-card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.delete-modal-enter-from,
.delete-modal-leave-to {
  opacity: 0;
}

.delete-modal-enter-from .delete-modal-card,
.delete-modal-leave-to .delete-modal-card {
  transform: scale(0.92) translateY(-8px);
  opacity: 0;
}

/* ── Keyframes ───────────────────────────────────────────────────────────── */
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>

